import { convertMedicalCertificateToPdf } from "./medicalCertificatePdf";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { getBytes, ref as storageRef } from "firebase/storage";
import { storage } from "../../../../firebase";
import { formatAnnualMaskPressureRange } from "../../Followup/annualMaskClinicalDataUtils";
import { LPH_ANNUAL_MASK_CERT_TEMPLATES, normalizeCertificateDoctor } from "./lphMedicalCertificateTemplates";

const MEDICAL_CERT_TEMPLATE_PATH = "templates/medical_cert_embedded";

const getMedicalCertTemplateBytes = (fileName) =>
  getBytes(storageRef(storage, `${MEDICAL_CERT_TEMPLATE_PATH}/${fileName}`));

const safeText = (value) => String(value || "").trim();

const getThaiDate = (value) => {
  const date = value && value.toDate ? value.toDate() : new Date(value);

  if (!date || Number.isNaN(date.getTime())) return "วันที่ -";

  return (
    "วันที่ " +
    date.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
  );
};

export const getAnnualMaskMedicalCertTemplate = (node, doctor) => {
  const normalizedNode = safeText(node).toLowerCase();
  if (!["lph", "ladprao hospital"].includes(normalizedNode)) {
    throw new Error("Annual mask medical certificates support LPH cases only.");
  }

  const template = LPH_ANNUAL_MASK_CERT_TEMPLATES[normalizeCertificateDoctor(doctor)];
  if (template) return template;

  throw new Error("Annual mask medical certificates support C. Vatcharee, S. Jindapa, P. Sirincha and I. Nutcha only.");
};

export const getAnnualMaskMedicalCertFileName = (patient = {}) => {
  const patientName = [patient.firstnameTh, patient.lastnameTh]
    .map(safeText)
    .filter(Boolean)
    .join(" ") || safeText(patient.hn) || "patient";

  return `AnnualMaskMedicalCert_${patientName}`.replace(
    /[^a-zA-Z0-9-_ก-๙.]/g,
    "_",
  ) + ".docx";
};

export const canGenerateAnnualMaskMedicalCert = (caseItem = {}) => {
  const patient = caseItem.patient || caseItem;
  try {
    getAnnualMaskMedicalCertTemplate(
      caseItem.nodeRef || caseItem.node || patient.nodeRef || patient.node,
      caseItem.doctorName || patient.doctor,
    );
    return true;
  } catch (error) {
    return false;
  }
};

// Uses the same LPH Annual 1 Year Mask templates exposed in Export > Medical
// Certificate. Keeping this generator shared prevents the emailed form from
// drifting from the operator-exported form.
export const generateAnnualMaskMedicalCertBlob = async ({
  patient = {},
  nodeRef,
  doctor = "",
  ahi = "",
  minSpo2 = "",
  minPressure = "",
  maxPressure = "",
  pressure = "",
  latestSleepStudyDate = "",
}) => {
  const selectedDoctor = safeText(doctor || patient.doctor);
  const templateFileName = getAnnualMaskMedicalCertTemplate(
    nodeRef === undefined ? patient.nodeRef || patient.node : nodeRef,
    selectedDoctor,
  );

  const content = await getMedicalCertTemplateBytes(templateFileName);
  const zip = new PizZip(content);
  const documentTemplate = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    delimiters: { start: "<", end: ">" },
  });

  const patientName = [patient.firstnameTh, patient.lastnameTh]
    .map(safeText)
    .filter(Boolean)
    .join(" ");

  documentTemplate.setData({
    DOCTOR_SCORED_DATE_TH: getThaiDate(new Date()),
    PATIENT_NAME: patientName,
    STUDY_DATE_TH: getThaiDate(latestSleepStudyDate),
    AHI: safeText(ahi) || safeText(patient.ahi) || "0",
    MIN_SPO2: safeText(minSpo2) || "-",
    PRESSURE:
      formatAnnualMaskPressureRange({
        minPressure,
        maxPressure,
        pressure,
      }) || "5-15",
  });
  documentTemplate.render();

  return documentTemplate.getZip().generate({
    type: "blob",
    compression: "DEFLATE",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
};

export const getAnnualMaskMedicalCertPdfFileName = (patient = {}) =>
  getAnnualMaskMedicalCertFileName(patient).replace(/\.docx$/i, ".pdf");

export const generateAnnualMaskMedicalCertPdfBlob = async (values) =>
  convertMedicalCertificateToPdf(await generateAnnualMaskMedicalCertBlob(values));
